import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import DispatchMap from "@/components/DispatchMap";
import { useAuth } from "@/context/AuthContext";
import { DiscBadge, PriorityBadge, StatusChip } from "@/components/badges";
import { Activity, AlertTriangle, Radio, Truck, GraduationCap, PlayCircle } from "lucide-react";

function Stat({ label, value, color, testid, icon: Icon }) {
  return (
    <div data-testid={testid}
      className="relative bg-[#141518] border border-[#2C2D35] px-4 py-3 rounded-sm overflow-hidden tac-hover hover:border-[#3A3B44]">
      <span className="absolute top-0 left-0 h-[3px] w-full" style={{ background: color }} />
      <div className="flex items-center justify-between">
        <span className="tac-label">{label}</span>
        {Icon && <Icon size={14} strokeWidth={1.75} style={{ color }} />}
      </div>
      <div className="font-mono font-bold text-3xl mt-1 tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function SectionBar({ icon: Icon, children }) {
  return (
    <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-display font-bold text-[11px] uppercase tracking-wider flex items-center gap-2">
      {Icon && <Icon size={13} strokeWidth={1.75} className="text-[#3B82F6]" />} {children}
    </div>
  );
}

export default function Dashboard() {
  const [m, setM] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [units, setUnits] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [assigned, setAssigned] = useState([]);

  const startScenario = (s, sessionId) => nav("/new-call", { state: { scenario: {
    key: s.key, title: s.title, disc: s.disc, priority: s.priority, address: s.address,
    description: s.description, callerName: s.caller_name, callerPhone: s.caller_phone,
    objective: s.objective, sessionId,
  } } });

  useEffect(() => {
    if (user?.env !== "training") return;
    client.get("/training/scenarios").then((r) => setScenarios(r.data)).catch(() => {});
    client.get("/training/my").then((r) => setAssigned(r.data)).catch(() => {});
  }, [user]);

  const load = async () => {
    const [mm, inc, un] = await Promise.all([
      client.get("/metrics/dashboard"),
      client.get("/incidents", { params: { active: true } }),
      client.get("/units"),
    ]);
    setM(mm.data); setIncidents(inc.data); setUnits(un.data);
  };

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  return (
    <Layout>
      <PageHeader title="Command Center" sub="Real-time common operating picture · auto-refresh 8s"
        right={<div className="font-mono text-[11px] text-[#22C55E] flex items-center gap-2 border border-[#22C55E]/30 bg-[#22C55E]/10 px-2.5 py-1 rounded-sm">
          <span className="live-dot w-2 h-2 rounded-full bg-[#22C55E] after:bg-[#22C55E]" />SYSTEMS NOMINAL</div>} />
      <div className="flex-1 overflow-y-auto p-6 grid-lines">
        {user?.env === "training" && assigned.length > 0 && (
          <div data-testid="assigned-training" className="mb-4 border border-[#8B5CF6]/50 bg-[#8B5CF6]/[0.08] rounded-sm anim-in">
            <div className="px-3 py-2 border-b border-[#8B5CF6]/30 font-display font-bold text-[11px] uppercase tracking-wider text-[#8B5CF6] flex items-center gap-2">
              <GraduationCap size={14} /> Assigned By Your Trainer · complete these scenarios
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 p-3">
              {assigned.map((s) => (
                <div key={s.session_id} className="border border-[#8B5CF6]/40 bg-[#141518] p-3 flex flex-col rounded-sm">
                  <div className="font-head font-semibold text-sm">{s.title}</div>
                  <div className="tac-label mt-0.5">{s.disc} · P{s.priority} · from {s.assigned_by}</div>
                  <div className="text-xs text-[#9CA3AF] mt-1.5 flex-1 leading-snug">{s.objective}</div>
                  <button data-testid={`assigned-start-${s.session_id}`} onClick={() => startScenario(s, s.session_id)}
                    className="mt-2 flex items-center justify-center gap-1.5 py-1.5 text-xs font-head bg-[#8B5CF6] text-white hover:bg-[#7c3aed] rounded-sm tac-hover">
                    <PlayCircle size={13} /> Start Assigned Call
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {user?.env === "training" && (
          <div data-testid="training-scenarios" className="mb-4 border border-[#F59E0B]/40 bg-[#F59E0B]/[0.06] rounded-sm">
            <div className="px-3 py-2 border-b border-[#F59E0B]/30 font-display font-bold text-[11px] uppercase tracking-wider text-[#F59E0B] flex items-center gap-2">
              <GraduationCap size={14} /> Practice Scenarios · self-guided calls (simulation only)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 p-3">
              {scenarios.map((s) => (
                <div key={s.key} className="border border-[#2C2D35] bg-[#141518] p-3 flex flex-col rounded-sm">
                  <div className="font-head font-semibold text-sm">{s.title}</div>
                  <div className="tac-label mt-0.5">{s.disc} · P{s.priority}</div>
                  <div className="text-xs text-[#9CA3AF] mt-1.5 flex-1 leading-snug">{s.objective}</div>
                  <button data-testid={`scenario-start-${s.disc}-${s.priority}`} onClick={() => startScenario(s)}
                    className="mt-2 flex items-center justify-center gap-1.5 py-1.5 text-xs font-head bg-[#F59E0B] text-black hover:bg-[#d97706] rounded-sm tac-hover">
                    <PlayCircle size={13} /> Begin Practice Call
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <Stat testid="stat-active" label="Active Incidents" value={m?.active ?? "–"} color="#F9FAFB" icon={Activity} />
          <Stat testid="stat-pending" label="Pending Dispatch" value={m?.pending ?? "–"} color="#F59E0B" icon={Radio} />
          <Stat testid="stat-priority1" label="P1 Critical" value={m?.priority1 ?? "–"} color="#DC2626" icon={AlertTriangle} />
          <Stat testid="stat-police" label="Police Active" value={m?.by_discipline?.police ?? "–"} color="#3B82F6" />
          <Stat testid="stat-fire" label="Fire Active" value={m?.by_discipline?.fire ?? "–"} color="#EF4444" />
          <Stat testid="stat-ems" label="EMS Active" value={m?.by_discipline?.ems ?? "–"} color="#10B981" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-[#2C2D35] rounded-sm overflow-hidden">
            <SectionBar icon={Truck}>Live Tactical Map · {units.length} Units · {incidents.length} Active</SectionBar>
            <DispatchMap units={units} incidents={incidents} height="440px"
              onSelectIncident={(i) => nav(`/incident/${i.id}`)} enableCoverage />
          </div>

          <div className="border border-[#2C2D35] flex flex-col rounded-sm overflow-hidden">
            <SectionBar icon={Activity}>Active Incident Queue</SectionBar>
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: 440 }}>
              {incidents.length === 0 && <div className="p-4 text-xs text-[#9CA3AF] font-mono">No active incidents.</div>}
              {incidents.map((i) => (
                <button key={i.id} data-testid={`queue-incident-${i.cc_number}`} onClick={() => nav(`/incident/${i.id}`)}
                  className="w-full text-left px-3 py-2.5 border-b border-[#2C2D35] hover:bg-[#141518] tac-hover">
                  <div className="flex items-center gap-2 mb-1">
                    <PriorityBadge priority={i.priority} />
                    <DiscBadge discipline={i.discipline} />
                    <span className="font-mono text-[10px] text-[#9CA3AF] ml-auto">{i.cc_number}</span>
                  </div>
                  <div className="text-sm font-head font-medium truncate">{i.call_type_label}</div>
                  <div className="font-mono text-[11px] text-[#9CA3AF] truncate">{i.location?.address}</div>
                  <div className="font-mono text-[10px] text-[#6B7280]" data-testid={`queue-received-${i.cc_number}`}>Received {i.created_at ? new Date(i.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <StatusChip status={i.status} />
                    <span className="font-mono text-[10px] text-[#9CA3AF]">{(i.assigned_units || []).join(", ") || "unassigned"}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
